// src/main-common/ChatbotPage/index.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './style.scss';
import { common } from '@mui/material/colors';
import { PDFDocument, rgb } from 'pdf-lib';
import fontkit from '@pdf-lib/fontkit';

const ChatbotPage = () => {
  const navigate = useNavigate();

  // --- State cho phần Upload & Camera ---
  const [imageSrc, setImageSrc] = useState(""); // Lưu base64 ảnh
  const [previewVisible, setPreviewVisible] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [videoStream, setVideoStream] = useState(null);
  const [currentFacingMode, setCurrentFacingMode] = useState("user");
  const [loading, setLoading] = useState(false);
  const showLoading = () => setLoading(true);
  const hideLoading = () => setLoading(false);
  const [currentTypingMessageIndex, setCurrentTypingMessageIndex] = useState(null);
  const [typedText, setTypedText] = useState("");
  const [fullText, setFullText] = useState("");
  const [typingSpeed, setTypingSpeed] = useState(5); 
  
  // --- State cho Chat ---
  const [messages, setMessages] = useState([
    {
      sender: "bot",
      text: "Chào bạn! Hãy upload hoặc chụp ảnh để tôi nhận diện và trò chuyện với bạn nhé."
    }
  ]);
  const [userInput, setUserInput] = useState("");
  const [conversation, setConversation] = useState([{ role: "system", content: "" }]);
  const [isImageAnalyzed, setIsImageAnalyzed] = useState(false);

  // Thêm state để lưu hướng dẫn và trigger nút PDF
  const [lastGuideMarkdown, setLastGuideMarkdown] = useState("");
  const [showExportPDF, setShowExportPDF] = useState(false);

  // --- Refs ---
  const videoRef = useRef(null);
  const fileInputRef = useRef(null);
  const chatMessagesRef = useRef(null);

  // --- Các hàm xử lý UPLOAD ẢNH ---
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) processImageFile(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) processImageFile(file);
  };

  const processImageFile = (file) => {
    if (file.size > 5 * 1024 * 1024) {
      alert("File vượt quá 5MB!");
      return;
    }
    // Giảm kích thước ảnh trước khi lưu (resizeImage sử dụng canvas)
    resizeImage(file, 800, 600, 0.7)
      .then((resizedDataUrl) => {
        setImageSrc(resizedDataUrl);
        setPreviewVisible(true);
        setIsImageAnalyzed(false);
        addBotMessage("Đã nhận được ảnh mới. Hãy gửi câu hỏi đầu tiên về ảnh này!");
      })
      .catch((error) => {
        console.error("Lỗi khi resize ảnh:", error);
        addBotMessage("Có lỗi xảy ra khi xử lý ảnh. Vui lòng thử lại.");
      });
  };

  const resizeImage = (file, maxWidth, maxHeight, quality = 0.7) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          let { width, height } = img;
          if (width > maxWidth || height > maxHeight) {
            const aspectRatio = width / height;
            if (width > height) {
              width = maxWidth;
              height = Math.round(maxWidth / aspectRatio);
            } else {
              height = maxHeight;
              width = Math.round(maxHeight * aspectRatio);
            }
          }
          const canvas = document.createElement("canvas");
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);
          const dataUrl = canvas.toDataURL("image/jpeg", quality);
          resolve(dataUrl);
        };
        img.onerror = (err) => reject(err);
        img.src = e.target.result;
      };
      reader.onerror = (err) => reject(err);
      reader.readAsDataURL(file);
    });
  };

  const clearPreview = () => {
    setPreviewVisible(false);
    setImageSrc("");
    setIsImageAnalyzed(false);
  };

  // --- Xử lý ảnh mẫu ---
  const sampleImages = [
    "/imageChat/sample1.jpg",
    "/imageChat/sample2.webp",
    "/imageChat/sample3.jpg",
    "/imageChat/sample4.jpg",
    "/imageChat/sample5.jpg",
    "/imageChat/sample6.jpg",
    "/imageChat/sample7.webp",
    "/imageChat/sample8.jpg",
    "/imageChat/sample9.jpg",
    "/imageChat/sample10.png",
  ];

  const handleSampleImageClick = (src) => {
    fetch(src)
      .then(response => response.blob())
      .then(blob => {
        const file = new File([blob], "sample-image.jpg", { type: blob.type });
        processImageFile(file);
      })
      .catch(error => {
        console.error("Lỗi khi tải ảnh mẫu:", error);
        addBotMessage("Có lỗi xảy ra khi tải ảnh mẫu. Vui lòng thử lại.");
      });
  };

  // --- Các hàm xử lý CAMERA ---
  const openCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: currentFacingMode } },
        audio: false
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setVideoStream(stream);
      setShowCamera(true);
    } catch (err) {
      console.error("Lỗi khi truy cập camera:", err);
      alert("Không thể truy cập camera. Vui lòng kiểm tra quyền truy cập.");
    }
  };

  const closeCamera = () => {
    if (videoStream) {
      videoStream.getTracks().forEach((track) => track.stop());
    }
    setShowCamera(false);
  };

  const switchCamera = async () => {
    if (videoStream) {
      videoStream.getTracks().forEach((track) => track.stop());
    }
    const newFacing = currentFacingMode === "user" ? "environment" : "user";
    setCurrentFacingMode(newFacing);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: newFacing } },
        audio: false
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setVideoStream(stream);
    } catch (err) {
      console.error("Lỗi khi chuyển đổi camera:", err);
      alert("Không thể chuyển đổi camera.");
    }
  };

  const captureImage = () => {
    if (!videoRef.current) return;
    const canvas = document.createElement("canvas");
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL("image/jpeg");
    closeCamera();
    processCapturedImage(dataUrl);
  };

  const processCapturedImage = (dataUrl) => {
    fetch(dataUrl)
      .then(res => res.blob())
      .then(blob => {
        const file = new File([blob], "captured-image.jpg", { type: blob.type });
        processImageFile(file);
      })
      .catch((error) => {
        console.error("Lỗi khi xử lý ảnh chụp:", error);
        addBotMessage("Có lỗi xảy ra khi xử lý ảnh chụp. Vui lòng thử lại.");
      });
  };

  // --- Các hàm xử lý CHAT ---
  const addBotMessage = (text) => {
    // Remove loading message
    setMessages(prev => {
      const messagesWithoutLoading = prev.filter(msg => msg.sender !== "loading");
      
      // Add an empty bot message that will be filled by the typewriter effect
      const newMessages = [...messagesWithoutLoading, { sender: "bot", text: "" }];
      
      // Store the index of the new bot message
      setCurrentTypingMessageIndex(newMessages.length - 1);
      
      // Store the full text for the typewriter effect
      setFullText(text);
      setTypedText("");
      // Nếu là hướng dẫn dệt, lưu lại markdown và bật nút PDF
      if (text && text.includes("Xin chào, tôi là trợ lý ảo Caobangceritage và đây là hướng dẫn của tôi:")) {
        setLastGuideMarkdown(text.replace('Xin chào, tôi là trợ lý ảo Caobangceritage và đây là hướng dẫn của tôi:', 'Xin chào, tôi là trợ lý ảo caobangceritage và đây là hướng dẫn của tôi:'));
        setShowExportPDF(true);
      } else {
        setShowExportPDF(false);
      }
      
      return newMessages;
    });
    scrollChatToBottom();
  };

  const addUserMessage = (text) => {
    setMessages(prev => [
      ...prev, 
      { sender: "user", text },
      // Add a special loading message that will show the spinner
      { sender: "loading", text: "" } 
    ]);
    scrollChatToBottom();
  };

  const scrollChatToBottom = () => {
    if (chatMessagesRef.current) {
      chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
    }
  };

  // Hàm chuyển markdown sang HTML (phiên bản đơn giản)
  const formatMarkdownToHTML = (markdown) => {
    const lines = markdown.split('\n');
    const htmlLines = [];
    let listBuffer = [];
    const flushList = () => {
      if (listBuffer.length > 0) {
        htmlLines.push('<ul>');
        htmlLines.push(...listBuffer);
        htmlLines.push('</ul>');
        listBuffer = [];
      }
    };
    lines.forEach(line => {
      const trimmedLine = line.trim();
      if (trimmedLine === '') {
        flushList();
        htmlLines.push('');
        return;
      }
      const headerMatch = trimmedLine.match(/^(#{1,6})\s+(.*)$/);
      if (headerMatch) {
        flushList();
        const level = headerMatch[1].length;
        htmlLines.push(`<h${level}>${headerMatch[2]}</h${level}>`);
        return;
      }
      if (trimmedLine.startsWith('-')) {
        listBuffer.push(`<li >${trimmedLine.replace(/^-+\s*/, '')}</li>`);
        return;
      }
      flushList();
      htmlLines.push(`<p>${trimmedLine}</p>`);
    });
    flushList();
    let html = htmlLines.join('\n');
    html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');
    return html;
  };

  const intentionTemplates = [
    "Viết hướng dẫn thực hiện dệt lại họa tiết này.",
    "Miêu tả bức ảnh một cách chi tiết.",
    "Họa tiết trong bức ảnh trên có thể thuộc dân tộc nào",
    "Hãy kể về lịch sử của họa tiết trong bức ảnh",
    "Hoạt tiết có màu chủ đạo là gì"
  ];

  const promtFix = `Bạn là một chuyên gia trong lĩnh vực văn hóa và nghệ thuật dệt may của các dân tộc thiểu số tại VIỆT NAM. Khi tôi tải lên hình ảnh về một họa tiết vải, nhiệm vụ của bạn là:
        Phân tích và mô tả chi tiết họa tiết, bao gồm:
        1. Màu sắc chủ đạo.
        2. Hình dáng hoặc biểu tượng đặc trưng (nếu có).
        3. Ý nghĩa văn hóa hoặc biểu tượng của họa tiết.
        4. Cung cấp thông tin về xuất xứ và truyền thống liên quan đến họa tiết hoặc loại vải này.
        5. Gợi ý thêm các ứng dụng hiện đại của họa tiết trong thiết kế thời trang hoặc nội thất (nếu có).
        Trả lời của bạn phải ngắn gọn, dễ hiểu và đầy đủ thông tin. Lưu ý, bạn chỉ được phép trả lời các câu hỏi liên quan tới họa tiết vải của Việt Nam, không trả lời các câu hỏi khác.
        Trước khi bạn đưa ra câu trả lời, bạn hãy nói "Xin chào, tôi là trợ lý ảo VabricAI và đây là câu trả lời của tôi:\n".
        LƯU Ý: BẠN CHỈ TRẢ LỜI CÁC CÂU HỎI LIÊN QUAN TỚI VẢI VÀ HỌA TIẾT VẢI, CÁC CÂU HỎI KHÁC SẼ KHÔNG ĐƯỢC TRẢ LỜI.
        Sau khi trả lời các ý trên, hãy trả lời câu hỏi của người dùng (nếu có) dưới đây:
`;

  const promtHuongdan = `Bạn là chuyên gia dệt truyền thống Việt Nam. Dựa trên hình ảnh họa tiết tôi gửi, hãy hướng dẫn chi tiết cách dệt lại họa tiết đó, chia theo từng bước rõ ràng từ chuẩn bị đến hoàn thiện:

1. **Chuẩn bị**
   - Nguyên liệu: loại sợi phù hợp (bông, len, tơ tằm...), ưu nhược điểm.
   - Dụng cụ: khung dệt, thoi, lược dệt, chỉ màu... và cách sử dụng.
   - Cách xử lý và nhuộm màu sợi (ưu tiên thuốc nhuộm tự nhiên).

2. **Thiết kế họa tiết**
   - Phác họa họa tiết từ ảnh.
   - Chuyển họa tiết vào hệ thống dệt (thủ công hoặc máy dệt).

3. **Lên khung & căng sợi**
   - Thiết lập khung dệt và căng sợi dọc đều.
   - Đảm bảo bố trí sợi chính xác theo họa tiết.

4. **Quy trình dệt chi tiết**
   - Mô tả từng công đoạn: quay sợi, mắc khung, tạo hoa văn, dệt.
   - Làm rõ cách phối màu, tạo hình, luồn sợi và ép hoa văn.
   - Ưu tiên ví dụ cụ thể, mẹo kỹ thuật nếu có.

5. **Hoàn thiện**
   - Kiểm tra, xử lý lỗi sau dệt.
   - Bảo quản vải và gợi ý ứng dụng (trang phục, nội thất...).

Chỉ trả lời về kỹ thuật dệt vải & họa tiết. Không trả lời các câu hỏi không liên quan.

Bắt đầu với: **"Xin chào, tôi là trợ lý ảo Cao Bang Verse và đây là hướng dẫn của tôi:"**

Yêu cầu:
- Ở phần Quy trình Dệt hãy viết nội dung thật chi tiết và đầy đủ, càng chi tiết càng tốt
- Trình bày chi tiết, giải thích rõ ràng, có ví dụ & mẹo.
- Lời văn chi tiết, đẩy đủ nội dung, nên có lời dẫn ở mỗi công đoạn
- Chỉ trả lời các nội dung liên quan đến vải & họa tiết thổ cẩm; không trả lời câu hỏi khác.
`;

  const recommendLink = `
    # Một số video hướng dẫn dệt thổ cẩm truyền thống mà bạn có thể tham khảo:
    - Hướng dẫn dệt khung cửi truyền thống: https://www.youtube.com/watch?v=UJDeQy_V3Wo
    - Máy dệt vải thổ cẩm: https://www.youtube.com/watch?v=ewzBMSGYuRg
    - Những điều cần biết về vải thổ cẩm: https://www.youtube.com/watch?v=oMn0DivBU1M
`;

  const processUserMessage = (message) => {
    if (!message.trim()) return;
    
    skipTypewriterEffect();
    
    addUserMessage(message);
    setConversation(prev => [...prev, { role: "user", content: message }]);
  
    let fullMessage = "";
    let token = 3000;
    if (message === "Viết hướng dẫn thực hiện dệt lại họa tiết này.") {
      fullMessage = promtHuongdan + message;
      token = 10000;
      // console.log(promtHuongdan);
      // console.log(promtFix);
    } else {
      fullMessage = promtFix + message;
    }
    callGpt4VisionApi(fullMessage, token);
    setUserInput("");
  };

  const callGpt4VisionApiLocal = async (userQuestion, token) => {
    try {
      showLoading(); // Hiển thị spinner loading 
      
      // Tạo AbortController để thiết lập timeout cho request
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
      }, 300000);
  
      // Chuẩn bị payload cho request
      const payload = {
        model: "gpt-4o",
        messages: [
          {
            role: "user",
            content: [
              {
                type: "text",
                text: userQuestion,
              },
              {
                type: "image_url",
                image_url: {
                  url: imageSrc, 
                  detail: "auto",
                },
              },
            ],
          },
        ],
        max_tokens: token,
      };
      
      // Gọi API endpoint trên server
      const response = await fetch("http://localhost:5000/api/chat-gpt-vision", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });
  
      clearTimeout(timeoutId);
  
      if (!response.ok) {
        throw new Error(`Error: ${response.status}`);
      }
  
      const data = await response.json();
      // console.log("API response từ OpenAI:", data);
      let answer = data?.choices?.[0]?.message?.content || "Không có nội dung trả về.";
      
      // Nếu sử dụng token 10000, có thể thêm một chuỗi gợi ý
      if (token === 10000) {
        answer = answer + recommendLink;
      }
      
      // Lưu câu trả lời vào state conversation hoặc hiển thị tin nhắn bot
      addBotMessage(answer);
      setIsImageAnalyzed(true);
    } catch (error) {
      console.error("Error calling GPT-4 Vision API:", error);
      addBotMessage("Hiện tại AI chưa trả lời được, hãy thử lại sau");
    } finally {
      hideLoading(); // Ẩn spinner loading
    }
  };
  const callGpt4VisionApi = async (userQuestion, token) => {
    try {
      showLoading(); // Hiển thị spinner loading 
      
      // Tạo AbortController để thiết lập timeout cho request
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
      }, 300000);
  
      // Chuẩn bị payload cho request
      const payload = {
        model: "gpt-4o",
        messages: [
          {
            role: "user",
            content: [
              {
                type: "text",
                text: userQuestion,
              },
              {
                type: "image_url",
                image_url: {
                  url: imageSrc, 
                  detail: "auto",
                },
              },
            ],
          },
        ],
        max_tokens: token,
      };
      
      // Gọi API endpoint trên server
      const response = await fetch("https://my-api-sage-ten.vercel.app/api/gpt-vision", {
        method: "POST", 
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });
  
      clearTimeout(timeoutId);
  
      if (!response.ok) {
        throw new Error(`Error: ${response.status}`);
      }
  
      const data = await response.json();
      // console.log("API response từ OpenAI:", data);
      let answer = data?.choices?.[0]?.message?.content || "Không có nội dung trả về.";
      
      // Nếu sử dụng token 10000, có thể thêm một chuỗi gợi ý
      if (token === 10000) {
        answer = answer + recommendLink;
      }
      
      // Lưu câu trả lời vào state conversation hoặc hiển thị tin nhắn bot
      addBotMessage(answer);
      setIsImageAnalyzed(true);
    } catch (error) {
      console.error("Error calling GPT-4 Vision API:", error);
      addBotMessage("Hiện tại AI chưa trả lời được, hãy thử lại sau");
    } finally {
      hideLoading(); // Ẩn spinner loading
    }
  };

  

  const skipTypewriterEffect = () => {
    if (currentTypingMessageIndex !== null) {
      setMessages(prev => {
        const updatedMessages = [...prev];
        updatedMessages[currentTypingMessageIndex] = {
          sender: "bot",
          text: fullText
        };
        return updatedMessages;
      });
      setCurrentTypingMessageIndex(null);
      setFullText("");
      setTypedText("");
    }
  };
  const handleChatKeyDown = (e) => {
    if (e.key === "Enter") processUserMessage(userInput);
  };

  useEffect(() => {
    let timeout;
    
    if (currentTypingMessageIndex !== null && typedText.length < fullText.length) {
      // Continue typing
      timeout = setTimeout(() => {
        const nextChar = fullText.charAt(typedText.length);
        setTypedText(prev => prev + nextChar);
        
        // Update the message in the messages array
        setMessages(prev => {
          const updatedMessages = [...prev];
          updatedMessages[currentTypingMessageIndex] = {
            sender: "bot",
            text: typedText + nextChar
          };
          return updatedMessages;
        });
        
        scrollChatToBottom();
      }, typingSpeed);
    } else if (typedText.length === fullText.length && currentTypingMessageIndex !== null) {
      // Finished typing
      setCurrentTypingMessageIndex(null);
      setFullText("");
    }
    
    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [typedText, fullText, currentTypingMessageIndex, typingSpeed]);

  // Hàm fetch font Unicode
  const fetchFont = async () => {
    const url = 'https://fonts.gstatic.com/s/notosans/v27/o-0IIpQlx3QUlC5A4PNb4g.ttf';
    const res = await fetch(url);
    return await res.arrayBuffer();
  };

  // Hàm chuyển markdown đơn giản sang các dòng text (giữ tiêu đề, danh sách, in đậm)
  const parseMarkdownToLines = (markdown) => {
    const lines = markdown.split('\n');
    let result = [];
    lines.forEach(line => {
      if (/^#+ /.test(line)) {
        // Tiêu đề
        result.push({ type: 'header', text: line.replace(/^#+ /, '') });
      } else if (/^- /.test(line)) {
        // Danh sách
        result.push({ type: 'list', text: '• ' + line.replace(/^- /, '') });
      } else if (/^\d+\. /.test(line)) {
        // Danh sách số
        result.push({ type: 'list', text: line });
      } else if (line.trim() === '') {
        result.push({ type: 'empty', text: '' });
      } else {
        // Đoạn văn thường
        result.push({ type: 'text', text: line });
      }
    });
    return result;
  };

  // Hàm tự động ngắt dòng cho PDF
  function wrapText(text, font, fontSize, maxWidth) {
    const words = text.split(' ');
    let lines = [];
    let currentLine = '';
    for (let word of words) {
      const testLine = currentLine ? currentLine + ' ' + word : word;
      const width = font.widthOfTextAtSize(testLine, fontSize);
      if (width > maxWidth && currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        currentLine = testLine;
      }
    }
    if (currentLine) lines.push(currentLine);
    return lines;
  }

  // Hàm xuất PDF
  const exportGuideToPDF = async () => {
    if (!imageSrc || !lastGuideMarkdown) return;
    const pdfDoc = await PDFDocument.create();
    pdfDoc.registerFontkit(fontkit);
    const page = pdfDoc.addPage([595.28, 841.89]); // A4 px
    const fontBytes = await fetchFont();
    const notoFont = await pdfDoc.embedFont(fontBytes);
    let y = 800;

    // --- HEADER ---
    // Logo (giả sử có ở public/logo512.png)
    let logoImg, logoDims;
    try {
      const logoUrl = window.location.origin + '/cbverse.png';
      const logoRes = await fetch(logoUrl);
      const logoBlob = await logoRes.blob();
      const logoDataUrl = await new Promise(res => { const reader = new FileReader(); reader.onloadend = () => res(reader.result); reader.readAsDataURL(logoBlob); });
      const logoBase64 = logoDataUrl.split(',')[1];
      logoImg = await pdfDoc.embedPng(logoBase64);
      logoDims = logoImg.scale(1);
      // Vẽ logo ở góc trái header
      page.drawImage(logoImg, { x: 40, y: 780, width: 80, height: 48 });
    } catch (e) {
      // Nếu không có logo thì bỏ qua
    }
    // Tiêu đề lớn, căn giữa
    page.drawText('Hướng dẫn dệt lại họa tiết', {
      x: 140,
      y: 800,
      size: 24,
      font: notoFont,
      color: rgb(0.1, 0.2, 0.6),
      // bold: true
    });
    // Ngày xuất file
    const today = new Date();
    const dateStr = today.toLocaleDateString('vi-VN');
    page.drawText(`Ngày xuất: ${dateStr}`, {
      x: 450,
      y: 810,
      size: 10,
      font: notoFont,
      color: rgb(0.3,0.3,0.3)
    });
    y = 760;

    // --- ẢNH ---
    const imgData = imageSrc.startsWith('data:') ? imageSrc : await fetch(imageSrc).then(r => r.blob()).then(blob => new Promise(res => { const reader = new FileReader(); reader.onloadend = () => res(reader.result); reader.readAsDataURL(blob); }));
    const base64 = imgData.split(',')[1];
    let img;
    try {
      img = await pdfDoc.embedJpg(base64);
    } catch {
      img = await pdfDoc.embedPng(base64);
    }
    const imgWidth = 260;
    const imgHeight = (img.height / img.width) * imgWidth;
    // Căn giữa ảnh
    page.drawImage(img, { x: (595.28 - imgWidth) / 2, y: y - imgHeight, width: imgWidth, height: imgHeight });
    y -= imgHeight + 30;

    // --- Nội dung markdown ---
    const lines = parseMarkdownToLines(lastGuideMarkdown);
    let fontSize = 13;
    const maxTextWidth = 470; // px, lề trái 60, phải 595-60=535, nên 470 là an toàn
    lines.forEach(({ type, text }) => {
      const cleanText = text.replace(/\*\*/g, '');
      if (y < 80) return; // tránh tràn trang (cơ bản)
      let drawLines = [];
      let color = rgb(0,0,0);
      let size = fontSize;
      let x = 60;
      if (type === 'header') {
        color = rgb(0.1,0.1,0.5);
        size = fontSize + 4;
        drawLines = wrapText(cleanText, notoFont, size, maxTextWidth);
      } else if (type === 'list') {
        x = 75;
        drawLines = wrapText(cleanText, notoFont, size, maxTextWidth - 15);
      } else if (type === 'empty') {
        y -= 10;
        return;
      } else {
        drawLines = wrapText(cleanText, notoFont, size, maxTextWidth);
      }
      for (let line of drawLines) {
        if (y < 80) break;
        page.drawText(line, { x, y, size, font: notoFont, color });
        y -= (type === 'header' ? 24 : type === 'list' ? 18 : 16);
      }
    });

    // --- FOOTER ---
    // Text bản quyền + trang
    page.drawText('caobangceritage.vn | Bản quyền © 2025', {
      x: 180,
      y: 30,
      size: 11,
      font: notoFont,
      color: rgb(0.5,0.1,0.1)
    });
    page.drawText('Trang 1', {
      x: 500,
      y: 30,
      size: 10,
      font: notoFont,
      color: rgb(0.4,0.4,0.4)
    });

    // Xuất file
    const pdfBytes = await pdfDoc.save();
    const blob = new Blob([pdfBytes], { type: 'application/pdf' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'huongdan-det-hoatiet.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  return (
  
      <div className="chatbotWrapper">
      {/* --- PHẦN BÊN TRÁI (ĐÃ THIẾT KẾ LẠI) --- */}
      

        {/* --- Phần Chat (Bên phải) --- */}
        <div className="chat-section">
          
          <div className="chat-header">
            <button className="theme-btn" onClick={() => navigate('/')}>
              Trang chủ
            </button>
            <h2>AI NHẬN DIỆN HỌA TIẾT</h2>
          </div>
          <div className="chat-messages" ref={chatMessagesRef}>
            {messages.map((msg, index) => {
              const isGuide = msg.sender === "bot" && msg.text.includes("Xin chào, tôi là trợ lý ảo Cao Bang Verse và đây là hướng dẫn của tôi:");
              return (
                <div
                  key={index}
                  className={msg.sender === "user" ? "user-message" : "bot-message"}
                  onClick={index === currentTypingMessageIndex ? skipTypewriterEffect : undefined}
                >
                  <div 
                    className="message-content"
                    dangerouslySetInnerHTML={{
                      __html: formatMarkdownToHTML(msg.text)
                    }}
                  ></div>
                  {isGuide && (
                    <div style={{ textAlign: 'center', margin: '16px 0' }}>
                      <button className="modal-btn pdf-btn" onClick={exportGuideToPDF}>
                        <span className="pdf-icon" style={{display:'inline-block',verticalAlign:'middle'}}>
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect x="3" y="3" width="18" height="18" rx="2" fill="#fff" stroke="#d32f2f" strokeWidth="2"/>
                            <path d="M7 8h10M7 12h10M7 16h6" stroke="#d32f2f" strokeWidth="2" strokeLinecap="round"/>
                          </svg>
                        </span>
                        Xuất hướng dẫn này ra PDF
                      </button>
                    </div>
                  )}
                  {index === currentTypingMessageIndex && (
                    <div className="typing-indicator">
                      <span className="cursor"></span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="chat-bottom">
            <div className="intention-templates">
              {intentionTemplates.map((template, index) => (
                <button
                  key={index}
                  className="intention-btn"
                  onClick={() => processUserMessage(template)}
                >
                  {template}
                </button>
              ))}
            </div>
            <div className="chat-input-area ">
              <input
                type="text"
                placeholder="Nhập tin nhắn..."
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                onKeyDown={handleChatKeyDown}
              />
              <button onClick={() => processUserMessage(userInput)}>Gửi</button>
            </div>
          </div>
        </div>
        <div className="leftPanelRedesign">

        {/* Thanh trên cùng: Nút Trang chủ */}
      

        {/* Khu vực "card" chứa Camera & Upload */}
        <div className="cardSection">

          {/* Khối chức năng chụp ảnh */}
          <div className="cameraBox">
            <h3>Chụp ảnh</h3>
            {!showCamera && (
              <button
                className="cameraToggleBtn"
                onClick={openCamera}
              >
                Mở camera
              </button>
            )}
            {showCamera && (
              <div className="cameraContainer">
                <video ref={videoRef} autoPlay playsInline></video>
                <div className="cameraButtons">
                  <button onClick={captureImage}>Chụp</button>
                  <button onClick={switchCamera}>Đổi camera</button>
                  <button onClick={closeCamera}>Đóng</button>
                </div>
              </div>
            )}
          </div>

          {/* Khối chức năng upload ảnh */}
          <div className="uploadBox">
            <h3>Upload ảnh</h3>
            {!previewVisible && (
              <div
                className="uploadDropArea"
                onClick={() => fileInputRef.current && fileInputRef.current.click()}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
              >
                <input
                  type="file"
                  accept="image/*"
                  ref={fileInputRef}
                  hidden
                  onChange={handleFileChange}
                />
                <div className="uploadIcon">
                  <img
                    src="https://cdn-icons-png.flaticon.com/512/109/109612.png"
                    alt="upload icon"
                  />
                </div>
                <p>Kéo thả hoặc click để upload</p>
                <p>(jpg/png &lt; 5MB)</p>
              </div>
            )}
            {previewVisible && (
              <div className="previewArea">
                <img src={imageSrc} alt="Preview" />
                <span className="closePreview" onClick={clearPreview}>
                  &times;
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Khu vực ảnh mẫu */}
        <div className="sampleSection">
          <h4>Ảnh ví dụ</h4>
          <div className="sampleList">
            {sampleImages.map((src, index) => (
              <img
                key={index}
                src={src}
                alt={`sample-${index}`}
                onClick={() => handleSampleImageClick(src)}
              />
            ))}
          </div>
        </div>
      </div>
      </div>

  );
};

export default ChatbotPage;
